// components/DashboardTile.jsx
// Dashboard card — matches CW Expense Manager aesthetic

import React from 'react';

/* ── Icon map ─────────────────────────────────────────────────── */

const ICONS = {
  projects: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  finance: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  msp: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  ),
  expense: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  ),
  sheets: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="3" x2="9" y2="21" />
      <line x1="15" y1="3" x2="15" y2="21" />
    </svg>
  ),
};

export default function DashboardTile({ name, description, url, icon, available, index = 0 }) {
  const iconElement = ICONS[icon] || ICONS.projects;

  const cardBase = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
    padding: '1.5rem',
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    transition: 'all 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
    overflow: 'hidden',
    animation: `fadeUp 0.45s cubic-bezier(0.22, 1, 0.36, 1) ${0.15 + index * 0.07}s both`,
  };

  const iconWrap = {
    width: 40,
    height: 40,
    borderRadius: 'var(--radius)',
    background: 'var(--accent-soft)',
    border: '1px solid rgba(99, 102, 241, 0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--accent)',
    transition: 'all 0.25s ease',
    flexShrink: 0,
  };

  /* ── Unavailable / coming-soon tile ──────────────────────────── */
  if (!available) {
    return (
      <div style={{ ...cardBase, opacity: 0.45, cursor: 'default' }}>
        <span style={{
          position: 'absolute',
          top: '1.1rem',
          right: '1.1rem',
          fontFamily: 'var(--font-mono)',
          fontSize: '0.6rem',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-light)',
          borderRadius: '6px',
          padding: '3px 10px',
        }}>
          Coming Soon
        </span>
        <div style={iconWrap}>{iconElement}</div>
        <span style={{
          fontFamily: 'var(--font-display)',
          fontSize: '1rem',
          fontWeight: 700,
          color: 'var(--text-primary)',
          letterSpacing: '-0.01em',
        }}>
          {name}
        </span>
        <span style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.8rem',
          fontWeight: 400,
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
        }}>
          {description}
        </span>
      </div>
    );
  }

  /* ── Available + accessible tile ─────────────────────────────── */
  return (
    <a
      href={url}
      style={{
        ...cardBase,
        cursor: 'pointer',
        textDecoration: 'none',
        color: 'inherit',
      }}
      onMouseEnter={e => {
        const card = e.currentTarget;
        card.style.borderColor = 'rgba(99, 102, 241, 0.3)';
        card.style.background = 'var(--bg-card-hover)';
        card.style.transform = 'translateY(-2px)';
        card.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(99, 102, 241, 0.08)';
        const glow = card.querySelector('[data-glow]');
        if (glow) glow.style.opacity = '1';
        const arrow = card.querySelector('[data-arrow]');
        if (arrow) {
          arrow.style.opacity = '1';
          arrow.style.transform = 'translateX(0)';
        }
        const iconEl = card.querySelector('[data-icon]');
        if (iconEl) {
          iconEl.style.background = 'var(--accent-glow)';
          iconEl.style.borderColor = 'rgba(99, 102, 241, 0.25)';
        }
      }}
      onMouseLeave={e => {
        const card = e.currentTarget;
        card.style.borderColor = 'var(--border)';
        card.style.background = 'var(--bg-card)';
        card.style.transform = 'translateY(0)';
        card.style.boxShadow = 'none';
        const glow = card.querySelector('[data-glow]');
        if (glow) glow.style.opacity = '0';
        const arrow = card.querySelector('[data-arrow]');
        if (arrow) {
          arrow.style.opacity = '0';
          arrow.style.transform = 'translateX(-6px)';
        }
        const iconEl = card.querySelector('[data-icon]');
        if (iconEl) {
          iconEl.style.background = 'var(--accent-soft)';
          iconEl.style.borderColor = 'rgba(99, 102, 241, 0.12)';
        }
      }}
    >
      {/* Corner glow on hover */}
      <div
        data-glow
        style={{
          position: 'absolute',
          top: '-50%',
          right: '-50%',
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle, var(--accent-soft), transparent 70%)',
          opacity: 0,
          transition: 'opacity 0.4s ease',
          pointerEvents: 'none',
        }}
      />

      <div data-icon style={iconWrap}>{iconElement}</div>

      <span style={{
        fontFamily: 'var(--font-display)',
        fontSize: '1rem',
        fontWeight: 700,
        color: 'var(--text-primary)',
        letterSpacing: '-0.01em',
        position: 'relative',
      }}>
        {name}
      </span>

      <span style={{
        fontFamily: 'var(--font-body)',
        fontSize: '0.8rem',
        fontWeight: 400,
        color: 'var(--text-secondary)',
        lineHeight: 1.5,
        position: 'relative',
      }}>
        {description}
      </span>

      {/* Arrow indicator */}
      <span
        data-arrow
        style={{
          position: 'absolute',
          bottom: '1.25rem',
          right: '1.25rem',
          fontSize: '1rem',
          color: 'var(--accent)',
          opacity: 0,
          transition: 'opacity 0.25s, transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
          transform: 'translateX(-6px)',
        }}
      >
        &rarr;
      </span>
    </a>
  );
}
